import { useQuery } from '@tanstack/react-query';

import { artiaProtectedApi } from '@/lib/api-client';

import { TherapeuticClassReportParams } from './use-therapeutic-class-report';

// Summary-specific response type
export interface TherapeuticClassReportSummary {
  numberOfDrugsAnalyzed: number;
  numberOfStatesAnalyzed: number;
  mostPreferredDrug: string;
  mostCommonPaType: string;
}

// Summary params (without pagination)
export interface TherapeuticClassReportSummaryParams extends Omit<TherapeuticClassReportParams, 'page'> {
  // No additional fields needed - just the filter params
}

const fetchTherapeuticClassReportSummary = async (
  params: TherapeuticClassReportSummaryParams
): Promise<TherapeuticClassReportSummary> => {
  // Prepare the request body for POST
  const requestBody = {
    classificationId: params.classificationId,
    drugIds: params.drugIds?.length ? params.drugIds : null,
    coverageTagIds: params.coverageTagIds?.length ? params.coverageTagIds : null,
    stateIds: params.stateIds?.length ? params.stateIds : null,
    poolIds: params.poolIds?.length ? params.poolIds : null,
    paTypeIds: params.paTypeIds?.length ? params.paTypeIds : null,
    pdlStatuses: params.pdlStatuses?.length ? params.pdlStatuses : null,
    boardMeetingTypeIds: params.boardMeetingTypeIds?.length ? params.boardMeetingTypeIds : null
  };

  try {
    const response = await artiaProtectedApi.post('reports/therapeutic-class/summary', requestBody);
    
    return response.data || response;
  } catch (error) {
    console.error('Error fetching therapeutic class report summary:', error);
    throw error;
  }
};

export const useTherapeuticClassReportSummary = (
  params: TherapeuticClassReportSummaryParams | null,
  enabled = true
) => {
  return useQuery({
    queryKey: ['therapeutic-class-report-summary', params],
    queryFn: () => fetchTherapeuticClassReportSummary(params!),
    enabled: enabled && !!params,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
